// Firebase initialization, emulators connection, and module exports.
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  connectAuthEmulator,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  connectFirestoreEmulator,
  Timestamp,
  serverTimestamp,
  runTransaction,
  doc,
} from 'firebase/firestore';
import {
  AUTH_EMULATOR_PORT,
  FIRESTORE_EMULATOR_PORT,
  runEmulatorStartupGuards,
} from '@/lib/emulatorGuards';

// Je Firebase configuratie
const firebaseConfig = {
  apiKey:
    import.meta.env.VITE_FIREBASE_API_KEY ||
    'AIzaSyDAKSwFwVV7OfrbgW8YQiOas9YE2TC19tM',
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    'todoes-7a167.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'todoes-7a167',
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
    'todoes-7a167.firebasestorage.app',
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '1051238296695',
  appId:
    import.meta.env.VITE_FIREBASE_APP_ID ||
    '1:1051238296695:web:9d20cb79efa745bf296a72',
  measurementId:
    import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-ZWD20NNQJ2',
};

const isLocal =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

// Bepaal of we de emulators moeten gebruiken
const useEmulators =
  import.meta.env.VITE_USE_EMULATORS === 'true' ||
  (isLocal && import.meta.env.VITE_USE_EMULATORS !== 'false');

if (useEmulators) {
  // Belangrijk voor popup communicatie in emulator omgevingen: de authDomain moet de lokale host zijn
  // We gebruiken hostname zonder poort, dat is meestal voldoende en veiliger voor Firebase.
  firebaseConfig.authDomain = window.location.hostname;
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Test/e2e: localStorage persistence zodat Playwright storageState de
// anonymous session kan hergebruiken (default IndexedDB zit niet in storageState).
function createAuth(firebaseApp) {
  if (import.meta.env.MODE === 'test') {
    try {
      return initializeAuth(firebaseApp, {
        persistence: browserLocalPersistence,
      });
    } catch {
      // HMR / dubbele init
      return getAuth(firebaseApp);
    }
  }
  return getAuth(firebaseApp);
}

const auth = createAuth(app);

const host = window.location.hostname;

// Root cause fix (init-order): connect Auth emulator IMMEDIATELY after createAuth,
// before initializeFirestore. Persistence restore is async; any work between
// getAuth/initializeAuth and connectAuthEmulator lets accounts:lookup hit
// identitytoolkit.googleapis.com (production) while Firestore uses the emulator.
if (useEmulators) {
  console.warn(`[Firebase] Connecting to emulators on ${host}...`);
  connectAuthEmulator(auth, `http://${host}:${AUTH_EMULATOR_PORT}`);
}

const db = initializeFirestore(app, {
  localCache: persistentLocalCache(),
});

if (useEmulators) {
  connectFirestoreEmulator(db, host, FIRESTORE_EMULATOR_PORT);
  // Fire-and-forget: orphan clear + reachability probes + emulatorConfig check
  void runEmulatorStartupGuards({ auth, host, useEmulators: true });
} else {
  console.warn('[Firebase] Connecting to production...');
}

// Export both instances and utilities
export { app, auth, db, Timestamp, serverTimestamp, doc, runTransaction };
